import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "shiftsContent", "tasksContent", "shiftsIcon", "tasksIcon",
    "activityCard", "cardBody", "cardIcon"
  ]

  static values = {
    url: String,
    newShiftLabel: { type: String, default: "Nieuwe dienst" },
    newTaskLabel: { type: String, default: "Nieuwe taak" },
    nameLabel: { type: String, default: "Naam" },
    roleLabel: { type: String, default: "Rol" },
    skillLabel: { type: String, default: "Skill" },
    minLevelLabel: { type: String, default: "Minimum level" },
    timesLabel: { type: String, default: "Tijden" },
    daysLabel: { type: String, default: "Dagen" },
    frequencyLabel: { type: String, default: "Frequentie" },
    everyoneLabel: { type: String, default: "Iedereen" },
    timesPlaceholder: { type: String, default: "bijv. 7:30–7:30" },
    recurrenceDaily: { type: String, default: "Dagelijks" },
    recurrenceWeekly: { type: String, default: "Wekelijks" },
    recurrenceEven: { type: String, default: "Even weken" },
    recurrenceOdd: { type: String, default: "Oneven weken" },
    recurrenceMonthly: { type: String, default: "1x per maand" },
    recurrenceCustom: { type: String, default: "Handmatig" },
    weightLabel: { type: String, default: "Gewicht (punten)" },
    qualifyingLabel: { type: String, default: "Kwalificerend:" }
  }

  nextCode = { shifts: 1, tasks: 1 }

  toggleSection(event) {
    const section = event.currentTarget.dataset.section
    const content = this[`${section}ContentTarget`]
    const icon = this[`${section}IconTarget`]

    if (content.classList.contains("hidden")) {
      content.classList.remove("hidden")
      icon.style.transform = "rotate(0deg)"
    } else {
      content.classList.add("hidden")
      icon.style.transform = "rotate(-90deg)"
    }
  }

  toggleCard(event) {
    if (event.target.closest("input[type='checkbox']")) return

    const card = event.target.closest("[data-onboarding-activities-target='activityCard']")
    if (!card) return

    const body = card.querySelector("[data-onboarding-activities-target='cardBody']")
    const icon = card.querySelector("[data-onboarding-activities-target='cardIcon']")
    if (!body) return

    const isHidden = body.classList.contains("hidden")
    body.classList.toggle("hidden", !isHidden)
    if (icon) icon.classList.toggle("rotate-180", isHidden)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  levelChanged(event) {
    const slider = event.target
    const display = slider.closest("div")?.querySelector(".level-display")
    if (!display) return

    const val = parseInt(slider.value)

    const colors = {
      1: "bg-blue-100 text-blue-700", 2: "bg-blue-100 text-blue-700",
      3: "bg-green-100 text-green-700", 4: "bg-green-100 text-green-700",
      5: "bg-yellow-100 text-yellow-700", 6: "bg-yellow-100 text-yellow-700",
      7: "bg-orange-100 text-orange-700", 8: "bg-orange-100 text-orange-700",
      9: "bg-red-100 text-red-700", 10: "bg-red-100 text-red-700"
    }

    display.textContent = `≥ ${val}`
    display.className = `level-display w-20 text-center text-xs font-semibold px-2 py-1 rounded-full ${colors[val] || "bg-gray-200 text-gray-500"}`
  }

  addActivity(event) {
    const type = event.currentTarget.dataset.activityType
    const container = type === "shifts" ? this.shiftsContentTarget : this.tasksContentTarget
    const prefix = type === "shifts" ? "S" : "T"
    const colorClass = type === "shifts" ? "text-green-600 focus:ring-green-500" : "text-blue-600 focus:ring-blue-500"

    const existingCodes = Array.from(container.querySelectorAll("[data-activity-code]"))
      .map(el => el.dataset.activityCode)
    let codeNum = this.nextCode[type] || 1
    let code
    do {
      code = `${prefix}_NEW_${codeNum}`
      codeNum++
    } while (existingCodes.includes(code))
    this.nextCode[type] = codeNum

    const card = document.createElement("div")
    card.className = "border border-gray-200 rounded-xl bg-white overflow-hidden ring-2 ring-[var(--pu-teal)]"
    card.dataset.onboardingActivitiesTarget = "activityCard"
    card.dataset.activityCode = code

    card.innerHTML = `
      <div class="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition"
           data-action="click->onboarding-activities#toggleCard">
        <input type="checkbox" name="${type}[${code}][enabled]" value="1"
               class="w-5 h-5 rounded ${colorClass}" checked
               data-action="click->onboarding-activities#stopPropagation">
        <span class="inline-block w-3 h-3 rounded-full flex-shrink-0" style="background-color: #6B7280"></span>
        <span class="font-mono text-sm font-bold text-gray-800">${code}</span>
        <span class="text-sm text-gray-400 flex-1 new-activity-name">${type === 'shifts' ? this.newShiftLabelValue : this.newTaskLabelValue}</span>
        <i class="fas fa-chevron-down text-gray-400 transition-transform duration-200 rotate-180"
           data-onboarding-activities-target="cardIcon"></i>
      </div>
      <div class="border-t border-gray-100 px-4 py-4 space-y-4"
           data-onboarding-activities-target="cardBody">
        <input type="hidden" name="${type}[${code}][code]" value="${code}">
        <input type="hidden" name="${type}[${code}][color]" value="#6B7280">
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">${this.nameLabelValue}</label>
          <input type="text" name="${type}[${code}][name]" value=""
                 placeholder="${type === 'shifts' ? this.newShiftLabelValue : this.newTaskLabelValue}"
                 class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-[var(--pu-teal)] focus:border-[var(--pu-teal)]"
                 data-action="input->onboarding-activities#nameInputChanged">
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">${this.roleLabelValue}</label>
          <select name="${type}[${code}][who]"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-[var(--pu-teal)] focus:border-[var(--pu-teal)]">
            <option value="">${this.everyoneLabelValue}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">${this.skillLabelValue}</label>
          <select name="${type}[${code}][skill]"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-[var(--pu-teal)] focus:border-[var(--pu-teal)]">
            <option value="any">${this.everyoneLabelValue}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">${this.minLevelLabelValue}</label>
          <div class="flex items-center gap-3">
            <input type="range" min="1" max="10" step="1" value="1"
                   name="${type}[${code}][min_level]"
                   class="flex-1 h-2 accent-[var(--pu-teal)] cursor-pointer"
                   data-action="input->onboarding-activities#levelChanged">
            <span class="level-display w-20 text-center text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-700">≥ 1</span>
          </div>
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">${this.timesLabelValue}</label>
          <input type="text" name="${type}[${code}][times]" value="" placeholder="${this.timesPlaceholderValue}"
                 class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-[var(--pu-teal)] focus:border-[var(--pu-teal)]">
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">${this.daysLabelValue}</label>
          <div class="flex flex-wrap gap-2">
            ${['ma','di','wo','do','vr','za','zo'].map(d =>
              `<label class="inline-flex items-center gap-1 cursor-pointer">
                <input type="checkbox" name="${type}[${code}][days][]" value="${d}"
                       class="w-4 h-4 rounded text-[var(--pu-teal)] focus:ring-[var(--pu-teal)]">
                <span class="text-xs font-medium text-gray-600">${d.charAt(0).toUpperCase() + d.slice(1)}</span>
              </label>`
            ).join('')}
          </div>
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">${this.frequencyLabelValue}</label>
          <select name="${type}[${code}][recurrence]"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-[var(--pu-teal)] focus:border-[var(--pu-teal)]">
            <option value="daily">${this.recurrenceDailyValue}</option>
            <option value="weekly">${this.recurrenceWeeklyValue}</option>
            <option value="even_weeks">${this.recurrenceEvenValue}</option>
            <option value="odd_weeks">${this.recurrenceOddValue}</option>
            <option value="monthly">${this.recurrenceMonthlyValue}</option>
            <option value="custom">${this.recurrenceCustomValue}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-600 mb-1">${this.weightLabelValue}</label>
          <input type="number" name="${type}[${code}][weight]" value="0" min="0" max="100" step="any"
                 class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-[var(--pu-teal)] focus:border-[var(--pu-teal)]">
        </div>
      </div>
    `

    const addButton = container.querySelector("[data-action*='addActivity']")?.closest(".pt-2")
    if (addButton) {
      container.insertBefore(card, addButton)
    } else {
      container.appendChild(card)
    }

    card.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  nameInputChanged(event) {
    const card = event.target.closest("[data-onboarding-activities-target='activityCard']")
    if (!card) return

    const label = card.querySelector(".new-activity-name")
    if (label) {
      label.textContent = event.target.value || (card.closest("[data-section='shifts']") ? this.newShiftLabelValue : this.newTaskLabelValue)
    }
  }

  async refreshPreview(event) {
    const card = event.target.closest("[data-onboarding-activities-target='activityCard']")
    if (!card || !this.urlValue) return

    const code = card.dataset.activityCode
    const skill = card.querySelector("[data-field='skill']")?.value || "any"
    const minLevel = card.querySelector("[data-field='min_level']")?.value || 1
    const role = card.querySelector("select[name*='[who]']")?.value || ""

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: new URLSearchParams({ skill, min_level: minLevel, role })
      })

      if (response.ok) {
        const html = await response.text()
        const preview = card.querySelector("[data-qualification-preview]")
        if (preview) {
          preview.innerHTML = `<span class="text-xs font-medium text-gray-400">${this.qualifyingLabelValue}</span><div class="mt-1 flex items-center gap-1 flex-wrap">${html}</div>`
        }
      }
    } catch (_e) {
      // Preview is informational only
    }
  }
}
